import { api } from './api';

/** Minimal player.js interface — Bunny ships the full SDK, we only need a slice. */
interface PlayerJsPlayer {
  on: (event: 'ready' | 'play' | 'timeupdate' | 'ended', cb: (data?: TimeUpdate) => void) => void;
  setCurrentTime: (seconds: number) => void;
}
interface TimeUpdate {
  seconds: number;
  duration: number;
}
declare global {
  interface Window {
    playerjs?: { Player: new (iframe: HTMLIFrameElement) => PlayerJsPlayer };
  }
}

const THROTTLE_SEC = 5; // server expects ≤ 1 progress write per 5s per material
const RESUME_THRESHOLD_SEC = 5; // skip resume if we're within 5s of the start
const RESUME_CUTOFF_PCT = 95; // already nearly finished — start fresh instead of resuming

/** Source-agnostic progress handlers so both materials and event recordings reuse the wrapper. */
export interface PlaybackHandlers {
  loadState: () => Promise<{ lastPosition: number; maxProgressPct: number }>;
  saveProgress: (sec: number, duration: number) => void; // fire-and-forget
}

/**
 * Wraps a Bunny iframe with player.js event handlers: tracks progress via the
 * given handlers, resumes from `lastPosition` on init, marks complete on end.
 * Returns a teardown callback for cleanup on unmount.
 */
export function attachTracking(iframe: HTMLIFrameElement, handlers: PlaybackHandlers): () => void {
  if (!window.playerjs) {
    console.warn('player.js not loaded; tracking disabled');
    return () => {};
  }

  const player = new window.playerjs.Player(iframe);
  let knownDuration = 0;
  let lastSent = -Infinity;
  let disposed = false;

  player.on('ready', async () => {
    if (disposed) return;
    try {
      const state = await handlers.loadState();
      if (state.lastPosition > RESUME_THRESHOLD_SEC && state.maxProgressPct < RESUME_CUTOFF_PCT) {
        player.setCurrentTime(state.lastPosition);
      }
    } catch {
      // Silent — fresh start is acceptable fallback
    }
  });

  player.on('timeupdate', (data) => {
    if (disposed || !data?.duration) return;
    knownDuration = data.duration;
    if (Math.abs(data.seconds - lastSent) < THROTTLE_SEC) return;
    lastSent = data.seconds;
    handlers.saveProgress(data.seconds, data.duration);
  });

  player.on('ended', () => {
    if (disposed || !knownDuration) return;
    handlers.saveProgress(knownDuration, knownDuration);
  });

  return () => {
    disposed = true;
  };
}

/** Progress tracking for a material's Bunny video. */
export function attachPlaybackTracking(iframe: HTMLIFrameElement, materialId: string): () => void {
  return attachTracking(iframe, {
    loadState: () => api.playbackState(materialId),
    saveProgress: (sec, duration) => {
      api.postProgress(materialId, { sec, duration }).catch(() => {});
    },
  });
}

/** Progress tracking for an event recording. */
export function attachEventRecordingTracking(
  iframe: HTMLIFrameElement,
  eventId: string,
): () => void {
  return attachTracking(iframe, {
    loadState: () => api.eventRecordingProgress(eventId),
    saveProgress: (sec, duration) => {
      api.eventRecordingProgressSave(eventId, sec, duration).catch(() => {});
    },
  });
}
